<template>
  <div class="ao-page">

    <!-- ===================================================== -->
    <!-- LOADING OVERLAY                                       -->
    <!-- ===================================================== -->
    <transition name="ao-fade">
      <div v-if="apiLoading" class="ao-loading-overlay">
        <div class="ao-loading-card">
          <div class="ao-logo ao-logo--lg">FM</div>
          <div class="ao-loading-text">{{ loadingMessage }}</div>
          <div class="ao-loading-bar">
            <div class="ao-loading-fill" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="ao-loading-tip">{{ currentTip }}</div>
        </div>
      </div>
    </transition>

    <div class="ao-wrap">

      <!-- ===================================================== -->
      <!-- HERO HEADER                                           -->
      <!-- ===================================================== -->
      <header class="ao-hero">
        <div class="ao-hero-actions">
          <b-button class="ao-btn-ghost-dark" @click="filtersVisible = !filtersVisible">
            <b-icon icon="funnel-fill"></b-icon>
            {{ filtersVisible ? 'Hide filters' : 'Show filters' }}
          </b-button>
          <b-button class="ao-btn-ghost-dark" @click="get_all_items_completed_invoices" :disabled="items_being_loaded || !hasMeaningfulFilter">
            <b-icon icon="arrow-clockwise"></b-icon> Refresh
          </b-button>
          <b-button class="ao-btn-ghost-dark" @click="applyAllFilters" :disabled="items_being_loaded || !hasMeaningfulFilter">
            <b-icon icon="check2-circle"></b-icon> Apply filters
          </b-button>
          <b-button class="ao-btn-ghost-dark" @click="resetToDefaultFilter" :disabled="items_being_loaded">
            <b-icon icon="arrow-counterclockwise"></b-icon> Clear filters
          </b-button>
        </div>
      </header>

      <!-- ===================================================== -->
      <!-- FILTER STRIP                                          -->
      <!-- ===================================================== -->
      <transition name="ao-fade-up">
        <section v-show="filtersVisible" class="ao-card ao-filterstrip">
          <div class="ao-filters-bar">

            <div class="ao-fb-item ao-fb-dates">
              <b-icon icon="calendar-range" class="ao-fb-pill-icon" style="margin-right:6px;"></b-icon>
              <b-input
                id="dates"
                size="sm"
                :placeholder="[searchForm.startDate, searchForm.endDate].filter(Boolean).join(' - ') || 'Select Date Range'"
                autocomplete="off"
                class="ao-fb-dateinput"
              ></b-input>
            </div>

            <div class="ao-fb-item ao-fb-search">
              <b-input-group class="ao-fb-inputgroup">
                <b-input-group-prepend>
                  <b-input-group-text><b-icon icon="search"></b-icon></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchForm.invoice_id"
                  placeholder="Invoice #, name, phone…"
                  class="ao-fb-input"
                  @keyup.enter="handleEnterSearch"
                ></b-form-input>
              </b-input-group>
            </div>

            <!-- Delivery status is FORCED to completed on this page; hide the dropdown -->

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="credit-card" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="paymentMethodsWithCounts" v-model="searchForm.payment_method" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="geo-alt" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="locationsWithCounts" v-model="searchForm.location_id" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="box-seam" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="orderTypeOptionsWithCounts" v-model="searchForm.order_type_filter" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="cash-coin" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="financePaymentProcessOptionsWithCounts" v-model="searchForm.finance_payment_process_filter" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="wallet2" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="paymentReceivedSimpleOptionsWithCounts" v-model="searchForm.payment_received_simple_filter" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

          </div>

          <div v-if="hasFetchedOnce && allItems.length > 0" class="ao-result-count">
            Total Completed Orders: <strong>{{ allItems.length }}</strong>
            <span v-if="filteredItems.length !== allItems.length" class="ao-result-count-filtered">
              | Showing: <strong>{{ filteredItems.length }}</strong>
              ({{ Math.round((filteredItems.length / allItems.length) * 100) }}%)
            </span>
          </div>

          <div v-if="!hasMeaningfulFilter" class="ao-result-count" style="color: #D4620F;">
            <b-icon icon="info-circle"></b-icon>
            Select at least one filter (date range, invoice #, payment method, location, order type, finance, or received) to load completed orders.
          </div>
        </section>
      </transition>

      <!-- ===================================================== -->
      <!-- EMPTY "PICK A FILTER" STATE (default)                 -->
      <!-- ===================================================== -->
      <div
        v-if="!hasFetchedOnce && !hasMeaningfulFilter"
        class="ao-card ao-empty"
        style="margin-top: 10px;"
      >
        <div class="ao-empty-icon"><b-icon icon="check2-circle" font-scale="2.4"></b-icon></div>
        <h3>Completed Orders</h3>
        <p>
          This list can get very large, so nothing is loaded by default.
          Pick a date range, invoice number, location, payment method, or any filter above and press
          <strong>Apply filters</strong> to load completed orders.
        </p>
      </div>

      <!-- ===================================================== -->
      <!-- TABLE                                                 -->
      <!-- ===================================================== -->
      <div v-else-if="hasFetchedOnce" style="margin-top: 10px;">
        <div v-if="filteredItems.length > 0" class="glass-table-container">
          <table class="ao-details-table ao-delivered-table">
            <colgroup>
              <col style="width: 9%;">   <!-- Invoice -->
              <col style="width: 14%;">  <!-- Customer -->
              <col style="width: 6%;">   <!-- Status -->
              <col style="width: 8%;">   <!-- Order Type -->
              <col style="width: 5%;">   <!-- Zipcode -->
              <col style="width: 8%;">   <!-- Sold From -->
              <col style="width: 5%;">   <!-- Ordered -->
              <col style="width: 5%;">   <!-- Delivered -->
              <col style="width: 6%;">   <!-- All Delivered -->
              <col style="width: 7%;">   <!-- Delivery Dates -->
              <col style="width: 15%;">  <!-- Payment Methods -->
              <col v-if="hasFinanceOrders" style="width: 6%;">  <!-- Finance Process -->
              <col v-if="hasCashOrCreditOrders" style="width: 6%;"> <!-- Finance Received -->
              <col style="width: 6%;">   <!-- Action -->
            </colgroup>
            <thead>
              <tr>
                <th class="ipt_tdhr2" @click="sortby('invoice_id')">INVOICE # <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('customer')">CUSTOMER <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('state')">STATUS <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('order_types_display')">ORDER TYPE <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('zipcode')">ZIPCODE <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('source_locsub')">SOLD FROM <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('nordered')"># ORDERED <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('ndelivered')"># DELIVERED <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('all_delivered')">ALL DELIVERED <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('delivery_dates')">DELIVERY DATES <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2">PAYMENT METHODS</th>
                <th class="ipt_tdhr2" @click="sortby('finance_status')" v-if="hasFinanceOrders">FINANCE PROCESS <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2" @click="sortby('deposit_status')" v-if="hasCashOrCreditOrders">FINANCE RECEIVED <span class="ao-sort-icon">↕</span></th>
                <th class="ipt_tdhr2">ACTION</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(item, idx) in filteredItems"
                :key="item.invoice_id + '_' + item.finance_status + '_' + item.deposit_status"
                class="fade-in-row"
                :class="{ 'discrepancy-row': hasDiscrepancy(item) }"
                :style="{ animationDelay: `${Math.min(idx, 20) * 0.02}s` }"
              >
                <td class="ipt_tdr3">
                  <span class="invoice-chip">{{ item.invoice_id }}</span>
                </td>
                <td class="ipt_tdr3" style="text-align: left;">
                  <div class="ao-cust-name">{{ item.fullname }}</div>
                  <div style="font-size: 15px; word-break: break-word;"># {{ item.phone }}</div>
                  <div style="font-size: 15px; word-break: break-all;">@ {{ item.email }}</div>
                </td>
                <td class="ipt_tdr3">
                  <span class="ao-mini-chip" :class="getStateClass(item.state)">{{ item.state }}</span>
                </td>
                <td class="ipt_tdr3" style="font-size: 16px;">
                  <div v-if="item.order_types_display">{{ item.order_types_display }}</div>
                  <div v-else class="ao-muted">N/A</div>
                </td>
                <td class="ipt_tdr3" style="font-size: 16px;">{{ item.zipcode }}</td>
                <td class="ipt_tdr3" style="font-size: 16px;">{{ getLocationName(item.source_locsub) }}</td>
                <td class="ipt_tdr3" style="font-size: 16px;" :style="{ 'background-color': get_bg_color(item) }">{{ item.nordered }}</td>
                <td class="ipt_tdr3" style="font-size: 16px;" :style="{ 'background-color': get_bg_color(item) }">{{ item.ndelivered }}</td>
                <td class="ipt_tdr3">
                  <span class="ao-mini-chip" :class="getAllDeliveredBadgeClass(item)">{{ getAllDeliveredStatus(item) }}</span>
                </td>
                <td class="ipt_tdr3">
                  <div
                    v-for="(ddate, ddidx) in (item.delivery_dates || [])"
                    :key="ddidx"
                    class="ao-deliv-chip"
                    style="display: block; margin: 2px auto; font-size: 11px;"
                  >
                    {{ ddate }}
                  </div>
                </td>
                <td class="ipt_tdr3" style="text-align: left;">
                  <div
                    v-for="(pm, pmidx) in (item.payment_methods || [])"
                    :key="pmidx"
                    class="ao-pm-box"
                    :style="{ background: get_pm_color(pm.value) }"
                  >
                    <div style="font-size: 16px;">
                      <strong>TYPE:</strong>
                      <span v-if="pm.value == 'cash'">{{ safeUpper(pm.value) }}</span>
                      <span v-else>{{ safeUpper(pm.value) }} - {{ safeUpper(pm.name) }}</span>
                    </div>

                    <div class="ao-pm-row">
                      <div style="font-size: 16px;"><strong>AMT:</strong> ${{ toMoney(pm.amount) }}</div>
                      <div class="mt-1" style="font-size: 16px;">
                        <strong>ACT:</strong>
                        <b-form-input
                          type="number"
                          step="0.01"
                          :value="getActualAmount(pm)"
                          @input="(val) => updateActualAmount(item, pm, pmidx, val)"
                          size="sm"
                          disabled
                          class="ao-pm-input"
                        ></b-form-input>
                      </div>
                    </div>

                    <div style="font-size: 16px;">
                      <strong>SHORT:</strong>
                      <span :style="{ color: getShortColor(pm), fontWeight: 'bold' }">
                        ${{ calculateShort(pm).toFixed(2) }}
                      </span>
                    </div>

                    <template v-if="pm.value == 'finance'">
                      <div class="mt-1">
                        <date-picker
                          v-model="pm.fund_date"
                          format="YYYY-MM-DD"
                          valueType="format"
                          class="ao-detail-datepicker"
                          placeholder="FUND DATE"
                          @change="update_pm_fund_date(item)"
                          disabled
                        ></date-picker>
                      </div>
                    </template>
                  </div>
                </td>
                <td class="ipt_tdr3" style="text-align: center;" v-if="hasFinanceOrders">
                  <b-form-checkbox
                    :checked="item.finance_status"
                    @change="(val) => update_finance_status_boolean(item, val)"
                    disabled
                  >
                    <span style="font-size: 15px;">{{ item.finance_status ? 'Processed' : 'Processed' }}</span>
                  </b-form-checkbox>
                </td>
                <td class="ipt_tdr3" style="text-align: center;" v-if="hasCashOrCreditOrders">
                  <b-form-checkbox
                    :checked="item.deposit_status"
                    @change="(val) => update_deposit_status_boolean(item, val)"
                    disabled
                  >
                    <span style="font-size: 15px;">{{ item.deposit_status ? 'Received' : 'Received' }}</span>
                  </b-form-checkbox>
                </td>
                <td class="ipt_tdr3" style="text-align: center;">
                  <span v-if="item.completed">
                    <b-button
                      size="sm"
                      class="ao-mini-btn"
                      @click="change_invoice_completed_status(item)"
                      v-if="isAdmin()"
                      :disabled="is_action_disabled()"
                    >
                      UNDO
                    </b-button>
                  </span>
                  <span v-else>
                    <b-button
                      size="sm"
                      class="ao-mini-btn is-primary"
                      @click="change_invoice_completed_status(item)"
                      :disabled="check_complete_allowed(item) == false || is_action_disabled()"
                    >
                      COMPLETE
                    </b-button>
                  </span>
                </td>
              </tr>
            </tbody>

            <tfoot v-if="filteredItems.length > 0">
              <tr>
                <td colspan="10" class="text-right" style="padding: 12px;">
                  TOTAL SHORT AMOUNT:
                </td>
                <td :style="{ color: getTotalShortColor(), fontSize: '16px', fontWeight: 'bold', padding: '12px' }" class="text-left">
                  ${{ calculateTotalShort().toFixed(2) }}
                </td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <div class="ao-card ao-empty">
            <div class="ao-empty-icon"><b-icon icon="inbox" font-scale="2.4"></b-icon></div>
            <h3>No completed orders match</h3>
            <p>Try a different date range or clear a filter.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- DISPUTE MODAL                                         -->
    <!-- ===================================================== -->
    <b-modal
      v-model="showDisputeModal"
      size="lg"
      @ok="submitDispute"
      @hidden="resetDisputeForm"
      ok-title="Submit Dispute"
      cancel-title="Cancel"
      content-class="ao-modal"
    >
      <template #modal-header>
        <div class="ao-modal-title">
          <span class="ao-modal-title-icon is-warn"><b-icon icon="exclamation-triangle-fill"></b-icon></span>
          <div>
            <div class="ao-modal-title-main">Mark order as disputed</div>
            <div class="ao-modal-title-sub">Provide details for the dispute</div>
          </div>
        </div>
      </template>

      <b-form class="ao-dispute-form">
        <b-form-group label="INVOICE ID">
          <b-form-input v-model="disputeForm.invoice_id" readonly class="ao-fb-input"></b-form-input>
        </b-form-group>

        <b-form-group label="DISPUTE TYPE">
          <b-form-select
            v-model="disputeForm.dispute_type"
            :options="disputeTypeOptions"
            required
            :state="disputeForm.dispute_type ? true : null"
            class="ao-detail-select-wide"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>-- Select Dispute Type --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="CUSTOMER STATEMENT">
          <b-form-radio-group v-model="disputeForm.customer_statement" required stacked>
            <b-form-radio value="received">Customer has received the merchandise</b-form-radio>
            <b-form-radio value="not_received">Customer has NOT received the merchandise</b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <b-form-group label="ISSUE TYPE">
          <b-form-select
            v-model="disputeForm.issue_type"
            :options="issueTypeOptions"
            required
            :state="disputeForm.issue_type ? true : null"
            class="ao-detail-select-wide"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>-- Select Issue Type --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="DISPUTE RESOLUTION">
          <b-form-select
            v-model="disputeForm.resolution"
            :options="resolutionOptions"
            required
            :state="disputeForm.resolution ? true : null"
            class="ao-detail-select-wide"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>-- Select Resolution --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="DID WE LOSE THE MONEY?">
          <b-form-radio-group v-model="disputeForm.money_lost" required>
            <b-form-radio :value="true">Yes, we lost the money</b-form-radio>
            <b-form-radio :value="false">No, we did not lose the money</b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <b-form-group label="DISPUTE DETAILS">
          <b-form-textarea
            v-model="disputeForm.details"
            rows="3"
            placeholder="Provide additional details about the dispute…"
            class="ao-fb-input"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="UPLOAD SUPPORTING IMAGE">
          <b-form-file
            v-model="disputeForm.image"
            accept="image/*"
            placeholder="Choose an image or drop it here…"
            drop-placeholder="Drop image here…"
            class="ao-fb-input"
          ></b-form-file>
          <div v-if="disputeForm.image_preview" class="mt-2">
            <img :src="disputeForm.image_preview" style="max-width: 200px; max-height: 200px;" class="img-thumbnail">
          </div>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import moment from "moment"
import Backer from "@/components/globals/Backer"
import Requestor from "@/utilities/Requestor"

import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

import Lightpick from 'lightpick';
import 'lightpick/css/lightpick.css';

const PARTIAL_DELIVERED_STATES = [0, 2, 3, 30, 9, 10, 11, 13, 4, 5, 6, 12];

function safeString(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(safeString).join(', ');
  if (typeof val === 'object') { try { return JSON.stringify(val); } catch (e) { return ''; } }
  try { return String(val); } catch (e) { return ''; }
}
function safeUpper(val) { return safeString(val).toUpperCase(); }
function safeLower(val) { return safeString(val).toLowerCase(); }
function safeNumber(val, fallback = 0) {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : fallback;
}
function safeMoney(val) { return safeNumber(val, 0).toFixed(2); }
function normalizePaymentMethod(pm) {
  if (!pm || typeof pm !== 'object') {
    return { value: '', name: '', amount: 0, actual_amount: null, fund_date: null, processed: null, received: null };
  }
  return {
    ...pm,
    value: pm.value !== null && pm.value !== undefined ? String(pm.value) : '',
    name: pm.name !== null && pm.name !== undefined ? safeString(pm.name) : '',
    amount: pm.amount !== null && pm.amount !== undefined ? pm.amount : 0,
    actual_amount: pm.actual_amount !== null && pm.actual_amount !== undefined ? pm.actual_amount : null,
    fund_date: pm.fund_date !== undefined ? pm.fund_date : null,
    processed: pm.processed !== undefined ? pm.processed : null,
    received: pm.received !== undefined ? pm.received : null
  };
}

export default {
  name: "AdminToolsCompletedOrdersPage",
  components: { Backer, flatPickr, Loading, DatePicker },
  data: function () {
    return {
      packet: { userDetails: {}, locsubsdict: {}, locations: [] },
      sort_asc: true,
      allItems: [],
      items: [],
      items_being_loaded: false,
      payment_methods_colors: {
        "cash": "#d4edda",
        "cc": "#e2e3e5",
        "finance": "#d1ecf1",
        "store_credit": "#fff3cd"
      },
      searchForm: {
        startDate: null,
        endDate: null,
        invoice_id: null,
        delivery_status: "completed",   // FORCED
        payment_method: null,
        location_id: null,
        order_type_filter: null,
        finance_payment_process_filter: null,
        payment_received_simple_filter: null
      },
      activeFilters: {
        startDate: null,
        endDate: null,
        invoice_id: null,
        delivery_status: "completed",
        payment_method: null,
        location_id: null,
        order_type_filter: null,
        finance_payment_process_filter: null,
        payment_received_simple_filter: null
      },
      filtersVisible: true,
      isMobile: false,
      formSubmitted: false,
      showDisputeModal: false,
      disputeForm: {
        invoice_id: null,
        dispute_type: null,
        customer_statement: null,
        issue_type: null,
        resolution: null,
        money_lost: null,
        details: null,
        image: null,
        image_preview: null
      },
      disputeTypeOptions: [
        { value: "customer", text: "🏠 Customer Dispute" },
        { value: "credit_card", text: "💳 Credit Card Company Dispute" },
        { value: "snap_finance", text: "🏦 Snap Finance Dispute" },
        { value: "merchant_service", text: "💼 Merchant Service Dispute" },
        { value: "attorney", text: "⚖️ Attorney Dispute" },
        { value: "court", text: "🏛️ Court Dispute" }
      ],
      issueTypeOptions: [
        { value: "fraud_charges", text: "Fraud Charges" },
        { value: "broken_merchandise", text: "Broken Merchandise" },
        { value: "wrong_item", text: "Wrong Item Received" },
        { value: "missing_item", text: "Missing Item" },
        { value: "not_as_described", text: "Not as Described" },
        { value: "delivery_issue", text: "Delivery Issue" },
        { value: "other", text: "Other" }
      ],
      resolutionOptions: [
        { value: "customer_favor", text: "Resolved in Customer's Favor" },
        { value: "business_favor", text: "Resolved in Business's Favor" },
        { value: "partial_refund", text: "Partial Refund Issued" },
        { value: "full_refund", text: "Full Refund Issued" },
        { value: "replacement", text: "Replacement Sent" },
        { value: "pending", text: "Pending Resolution" }
      ],
      apiLoading: false,
      loadingMessage: 'Loading Completed Orders',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,
      autoSaveTimeout: null,
      tips: [
        "✅ Loading completed orders...",
        "🚚 Fetching delivery data...",
        "💰 Processing payment info...",
        "📊 Calculating statistics...",
        "🔄 Syncing with server...",
        "✨ Organizing order list..."
      ],
      datePickerInstance: null,
      filterDebounceTimer: null,
      searchMode: false,
      PARTIAL_DELIVERED_STATES,
      // NEW: gate flags
      hasFetchedOnce: false
    }
  },
  computed: {
    currentTip() { return this.tips[this.currentTipIndex] || this.tips[0]; },

    hasMeaningfulFilter() {
      const f = this.searchForm;
      return !!(
        (f.startDate && f.endDate) ||
        (f.invoice_id && String(f.invoice_id).trim() !== '') ||
        f.payment_method ||
        f.location_id ||
        f.order_type_filter ||
        f.finance_payment_process_filter ||
        f.payment_received_simple_filter
      );
    },

    hasFinanceOrders: function () {
      return this.allItems.some(item =>
        Array.isArray(item.payment_methods) &&
        item.payment_methods.some(pm => pm.value === 'finance')
      );
    },
    hasCashOrCreditOrders: function () {
      return this.allItems.some(item =>
        Array.isArray(item.payment_methods) &&
        item.payment_methods.some(pm => pm.value === 'cash' || pm.value === 'cc')
      );
    },
    financePaymentProcessOptionsWithCounts() {
      let newCount = 0, processedCount = 0, discrepancyCount = 0, totalFinance = 0;
      this.allItems.forEach(item => {
        const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
        const hasFinancePayment = pms.some(pm => pm.value === 'finance');
        if (hasFinancePayment) {
          totalFinance++;
          const hasDiscrepancy = pms.some(pm => {
            if (pm.value !== 'finance') return false;
            const expectedAmount = safeNumber(pm.amount, 0);
            const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null)
              ? safeNumber(pm.actual_amount, 0) : expectedAmount;
            return actualAmount < expectedAmount;
          });
          if (hasDiscrepancy) discrepancyCount++;
          else if (item.finance_status === true) processedCount++;
          else newCount++;
        }
      });
      const options = [];
      options.push({ value: null, text: `ALL (${totalFinance})` });
      options.push({ value: "false", text: `New (${newCount})` });
      options.push({ value: "true", text: `Processed (${processedCount})` });
      if (discrepancyCount > 0) options.push({ value: "discrepancy", text: `Discrepancy (${discrepancyCount})` });
      return options;
    },
    paymentReceivedSimpleOptionsWithCounts() {
      let receivedCount = 0, notReceivedCount = 0;
      this.allItems.forEach(item => {
        const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
        const hasFinancePayment = pms.some(pm => pm.value === 'finance');
        if (hasFinancePayment) {
          const financePayments = pms.filter(pm => pm.value === 'finance');
          const allReceived = financePayments.every(pm => pm.payment_received === true);
          if (allReceived) receivedCount++;
          else notReceivedCount++;
        }
      });
      const options = [];
      const totalFinance = receivedCount + notReceivedCount;
      options.push({ value: null, text: `ALL (${totalFinance})` });
      options.push({ value: "true", text: `Received (${receivedCount})` });
      options.push({ value: "false", text: `Not Received (${notReceivedCount})` });
      return options;
    },
    paymentMethodsWithCounts() {
      const counts = { cash: 0, cc: 0, finance: 0, store_credit: 0 };
      const invoiceTracker = { cash: new Set(), cc: new Set(), finance: new Set(), store_credit: new Set() };
      this.allItems.forEach(item => {
        const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
        pms.forEach(pm => {
          if (counts.hasOwnProperty(pm.value)) invoiceTracker[pm.value].add(item.invoice_id);
        });
      });
      for (const [key, set] of Object.entries(invoiceTracker)) counts[key] = set.size;
      const options = [{ value: null, text: `ALL (${this.allItems.length})` }];
      for (const [key, count] of Object.entries(counts)) {
        if (count > 0) options.push({ value: key, text: `${safeUpper(key)} INVOLVED (${count})` });
      }
      return options;
    },
    locationsWithCounts() {
      const locationCounts = {};
      this.allItems.forEach(item => {
        const locationName = this.getLocationName(item.source_locsub);
        locationCounts[locationName] = (locationCounts[locationName] || 0) + 1;
      });
      const options = [{ value: null, text: `ALL (${this.allItems.length})` }];
      const sortedLocations = Object.entries(locationCounts).sort((a, b) => b[1] - a[1]);
      for (const [name, count] of sortedLocations) {
        options.push({ value: name, text: `${name} (${count})` });
      }
      return options;
    },
    orderTypeOptionsWithCounts() {
      const orderTypeCounts = {};
      this.allItems.forEach(item => {
        if (Array.isArray(item.order_types) && item.order_types.length) {
          item.order_types.forEach(type => {
            if (type) orderTypeCounts[type] = (orderTypeCounts[type] || 0) + 1;
          });
        }
      });
      const options = [{ value: null, text: `ALL (${this.allItems.length})` }];
      const sortedTypes = Object.entries(orderTypeCounts).sort((a, b) => b[1] - a[1]);
      for (const [type, count] of sortedTypes) {
        options.push({ value: type, text: `${safeUpper(type)} (${count})` });
      }
      return options;
    },
    filteredItems: function () {
      let filtered = [...this.allItems];

      const isSearchingByInvoice = this.searchForm.invoice_id && String(this.searchForm.invoice_id).trim() !== '';
      this.searchMode = isSearchingByInvoice;

      if (this.searchForm.payment_method) {
        filtered = filtered.filter(item =>
          Array.isArray(item.payment_methods) &&
          item.payment_methods.some(pm => pm.value === this.searchForm.payment_method)
        );
      }

      if (this.searchForm.location_id) {
        filtered = filtered.filter(item => {
          const locationName = this.getLocationName(item.source_locsub);
          return locationName === this.searchForm.location_id;
        });
      }

      if (this.searchForm.order_type_filter) {
        filtered = filtered.filter(item =>
          Array.isArray(item.order_types) &&
          item.order_types.includes(this.searchForm.order_type_filter)
        );
      }

      if (this.searchForm.finance_payment_process_filter && !this.searchMode) {
        if (this.searchForm.finance_payment_process_filter === 'discrepancy') {
          filtered = filtered.filter(item => {
            const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
            return pms.some(pm => {
              const expectedAmount = safeNumber(pm.amount, 0);
              const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null)
                ? safeNumber(pm.actual_amount, 0) : expectedAmount;
              return actualAmount < expectedAmount;
            });
          });
        } else {
          const filterValue = this.searchForm.finance_payment_process_filter === 'true';
          filtered = filtered.filter(item => {
            const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
            const hasFinancePayment = pms.some(pm => pm.value === 'finance');
            if (!hasFinancePayment) return false;
            return item.finance_status === filterValue;
          });
        }
      }

      if (this.searchForm.payment_received_simple_filter && !this.searchMode) {
        const filterValue = this.searchForm.payment_received_simple_filter === 'true';
        filtered = filtered.filter(item => {
          const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
          if (pms.length === 0) return false;
          return pms.every(pm => {
            if (pm.value === 'finance') return item.deposit_status === filterValue;
            if (pm.value === 'cash' || pm.value === 'cc') {
              if (pm.received !== undefined && pm.received !== null) return pm.received === filterValue;
              const expectedAmount = safeNumber(pm.amount, 0);
              const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null)
                ? safeNumber(pm.actual_amount, 0) : expectedAmount;
              return actualAmount >= expectedAmount;
            }
            return true;
          });
        });
      }

      filtered = filtered.filter(item => this.filterByDateRange(item));

      if (this.searchForm.invoice_id) {
        const searchTerm = safeLower(this.searchForm.invoice_id).trim();
        filtered = filtered.filter(item => safeLower(item.invoice_id).includes(searchTerm));
      }

      // Extra client-side safety: only completed
      filtered = filtered.filter(item => item.completed === true);

      return filtered;
    },
    totalShortAmount: function () {
      let total = 0;
      this.filteredItems.forEach(item => {
        const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
        pms.forEach(pm => { total += this.calculateShort(pm); });
      });
      return total;
    }
  },
  watch: {
    searchForm: {
      handler() {
        this.activeFilters = {
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate,
          invoice_id: this.searchForm.invoice_id,
          delivery_status: "completed",
          payment_method: this.searchForm.payment_method,
          location_id: this.searchForm.location_id,
          order_type_filter: this.searchForm.order_type_filter,
          finance_payment_process_filter: this.searchForm.finance_payment_process_filter,
          payment_received_simple_filter: this.searchForm.payment_received_simple_filter
        };
      },
      deep: true
    },
    allItems: {
      handler() { this.$forceUpdate(); },
      deep: true
    }
  },
  methods: {
    safeString,
    safeUpper,
    safeLower,

    toMoney(val) { return safeMoney(val); },

    getLocationName(locsub) {
      const dict = this.packet && this.packet.locsubsdict ? this.packet.locsubsdict : {};
      if (locsub === null || locsub === undefined) return 'Unknown';
      const name = dict[locsub];
      return (name === null || name === undefined || name === '') ? 'Unknown' : String(name);
    },
    isAdmin() {
      return !!(this.packet && this.packet.userDetails && this.packet.userDetails.userrole === 'admin');
    },
    handleEnterSearch() {
      this.applyAllFilters();
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    },
    getActualAmount: function (pm) {
      if (!pm) return 0;
      if (pm.actual_amount !== undefined && pm.actual_amount !== null) return pm.actual_amount;
      return pm.amount !== undefined && pm.amount !== null ? pm.amount : 0;
    },
    calculateShort: function (pm) {
      if (!pm) return 0;
      const expectedAmount = safeNumber(pm.amount, 0);
      const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null)
        ? safeNumber(pm.actual_amount, 0) : expectedAmount;
      return expectedAmount - actualAmount;
    },
    calculateTotalShort: function () { return this.totalShortAmount; },
    getShortColor: function (pm) {
      const short = this.calculateShort(pm);
      if (short > 0) return '#dc3545';
      if (short < 0) return '#28a745';
      return '#6c757d';
    },
    getTotalShortColor: function () {
      const total = this.totalShortAmount;
      if (total > 0) return '#FF7B6B';
      if (total < 0) return '#7FDACF';
      return '#ffffff';
    },
    hasDiscrepancy: function (item) {
      const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
      return pms.some(pm => {
        const expectedAmount = safeNumber(pm.amount, 0);
        const actualAmount = (pm.actual_amount !== undefined && pm.actual_amount !== null)
          ? safeNumber(pm.actual_amount, 0) : expectedAmount;
        return actualAmount < expectedAmount;
      });
    },
    updateActualAmount: function (item, pm, pmidx, value) {
      const actualValue = safeNumber(value, 0);
      this.$set(pm, 'actual_amount', actualValue);
      if (this.autoSaveTimeout) clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = setTimeout(() => { this.savePaymentMethod(item); }, 1000);
    },
    savePaymentMethod: function (item) {
      let that = this;
      let path = "/api/sales/update_payment_method/";
      let data = { invoice_id: item.invoice_id, payment_methods: item.payment_methods };
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else if (response && response.data && response.data.result) {
          that.$bvToast.toast("Payment method updated successfully.", { title: "Success", autoHideDelay: 1500, variant: "success" });
        }
      };
      let f = function (error) {
        console.error('Error updating payment method:', error);
        that.$bvToast.toast("Error updating payment method. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
      };
      Requestor.postAsyncP(path, data, this, s, f);
    },
    onFilterChange() {
      this.activeFilters = {
        startDate: this.searchForm.startDate,
        endDate: this.searchForm.endDate,
        invoice_id: this.searchForm.invoice_id,
        delivery_status: "completed",
        payment_method: this.searchForm.payment_method,
        location_id: this.searchForm.location_id,
        order_type_filter: this.searchForm.order_type_filter,
        finance_payment_process_filter: this.searchForm.finance_payment_process_filter,
        payment_received_simple_filter: this.searchForm.payment_received_simple_filter
      };
    },
    applyAllFilters: function () {
      if (!this.hasMeaningfulFilter) {
        this.$bvToast.toast(
          "Select at least one filter (date range, invoice #, payment method, etc.) to load completed orders.",
          { title: "Pick a filter", autoHideDelay: 3000, variant: "info" }
        );
        return;
      }
      if (this.searchForm.startDate && this.searchForm.endDate) {
        let diff = Math.abs(moment(this.searchForm.endDate).diff(moment(this.searchForm.startDate), 'days'));
        if (diff > 365) {
          this.$bvToast.toast("Cannot search more than 365 days at once.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
          return;
        }
      }
      this.searchMode = !!(this.searchForm.invoice_id && String(this.searchForm.invoice_id).trim() !== '');
      this.activeFilters = {
        startDate: this.searchForm.startDate,
        endDate: this.searchForm.endDate,
        invoice_id: this.searchForm.invoice_id,
        delivery_status: "completed",
        payment_method: this.searchForm.payment_method,
        location_id: this.searchForm.location_id,
        order_type_filter: this.searchForm.order_type_filter,
        finance_payment_process_filter: this.searchMode ? null : this.searchForm.finance_payment_process_filter,
        payment_received_simple_filter: this.searchMode ? null : this.searchForm.payment_received_simple_filter
      };
      this.get_all_items_completed_invoices();
      this.$bvToast.toast("Filters applied", { title: "Success", autoHideDelay: 1500, variant: "success" });
    },
    resetToDefaultFilter: function () {
      this.searchMode = false;
      this.searchForm = {
        startDate: null, endDate: null, invoice_id: null, delivery_status: "completed",
        payment_method: null, location_id: null, order_type_filter: null,
        finance_payment_process_filter: null, payment_received_simple_filter: null
      };
      this.activeFilters = {
        startDate: null, endDate: null, invoice_id: null, delivery_status: "completed",
        payment_method: null, location_id: null, order_type_filter: null,
        finance_payment_process_filter: null, payment_received_simple_filter: null
      };
      if (this.datePickerInstance) {
        this.datePickerInstance.setDateRange(null, null);
        const el = document.getElementById('dates');
        if (el) el.value = '';
      }
      this.allItems = [];
      this.items = [];
      this.hasFetchedOnce = false;
      this.$bvToast.toast("Cleared. Select a filter to view completed orders.", { title: "Reset", autoHideDelay: 2000, variant: "info" });
    },
    filterByDateRange: function (item) {
      if (!this.searchForm.startDate && !this.searchForm.endDate) return true;
      try {
        if (!item.ts) return true;
        const itemDate = moment(item.ts);
        if (!itemDate.isValid()) return true;
        let startDate = this.searchForm.startDate ? moment(this.searchForm.startDate) : null;
        let endDate = this.searchForm.endDate ? moment(this.searchForm.endDate) : null;
        if (startDate && !startDate.isValid()) startDate = null;
        if (endDate && !endDate.isValid()) endDate = null;
        if (startDate && endDate) return itemDate.isBetween(startDate, endDate, 'day', '[]');
        else if (startDate) return itemDate.isSameOrAfter(startDate, 'day');
        else if (endDate) return itemDate.isSameOrBefore(endDate, 'day');
        return true;
      } catch (e) {
        console.error('Error filtering by date:', e);
        return true;
      }
    },
    startApiLoading(message = 'Loading Completed Orders') {
      this.apiCallStack++;
      if (!this.apiLoading) this.startLoadingAnimation(message);
      else this.loadingMessage = message;
    },
    stopApiLoading() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0 && this.apiLoading) this.stopLoadingAnimation(true);
    },
    startLoadingAnimation(message = 'Loading Completed Orders') {
      this.apiLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) this.currentTipIndex++;
      }, 2000);
      let messages = ['Loading Completed Orders', 'Fetching Data', 'Processing Orders', 'Updating Status', 'Finalizing'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) { msgIndex++; this.loadingMessage = messages[msgIndex]; }
      }, 1800);
    },
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      if (this.msgInterval) clearInterval(this.msgInterval);
      this.loadingInterval = null;
      this.tipInterval = null;
      this.msgInterval = null;
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => { this.apiLoading = false; }, 500);
      } else {
        this.apiLoading = false;
      }
    },
    getStateClass(state) {
      const stateStr = safeLower(state);
      if (stateStr === 'delivered') return 'is-teal';
      if (stateStr === 'partial') return 'is-warn';
      if (stateStr === 'pending') return 'is-cc';
      return '';
    },
    handleResize() { this.isMobile = window.innerWidth < 768; },
    resetDisputeForm() {
      this.disputeForm = {
        invoice_id: null, dispute_type: null, customer_statement: null, issue_type: null,
        resolution: null, money_lost: null, details: null, image: null, image_preview: null
      };
      this.formSubmitted = false;
    },
    update_finance_status_boolean: function (item, newValue) {
      let that = this;
      const originalValue = item.finance_status;
      this.$set(item, 'finance_status', newValue);
      let data = { invoice_id: item.invoice_id, finance_status: newValue };
      let path = "/api/sales/update_finance_status_boolean/";
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          that.$set(item, 'finance_status', originalValue);
        } else {
          that.$bvToast.toast("Finance status updated.", { title: "Success", autoHideDelay: 1500, variant: "success" });
        }
      };
      let f = function (error) {
        console.error('Error updating finance status:', error);
        that.$bvToast.toast("Error updating finance status.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        that.$set(item, 'finance_status', originalValue);
      };
      Requestor.postAsyncP(path, data, this, s, f);
    },
    update_deposit_status_boolean: function (item, newValue) {
      let that = this;
      const originalValue = item.deposit_status;
      this.$set(item, 'deposit_status', newValue);
      let data = { invoice_id: item.invoice_id, deposit_status: newValue };
      let path = "/api/sales/update_deposit_status_boolean/";
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          that.$set(item, 'deposit_status', originalValue);
        } else {
          that.$bvToast.toast("Deposit status updated.", { title: "Success", autoHideDelay: 1500, variant: "success" });
        }
      };
      let f = function (error) {
        console.error('Error updating deposit status:', error);
        that.$bvToast.toast("Error updating deposit status.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        that.$set(item, 'deposit_status', originalValue);
      };
      Requestor.postAsyncP(path, data, this, s, f);
    },
    updateOrderTypeOptions: function () {
      // counts are computed dynamically; no-op kept for API parity
    },
    getAllDeliveredStatus: function (item) {
      if (!item) return 'No';
      const nordered = safeNumber(item.nordered, 0);
      const ndelivered = safeNumber(item.ndelivered, 0);
      const itemStates = Array.isArray(item.item_states) ? item.item_states : [];
      if (nordered === 0) return 'Yes';
      const hasPartialDeliveredItem = itemStates.some(s => PARTIAL_DELIVERED_STATES.includes(Number(s)));
      if (ndelivered === 0) return hasPartialDeliveredItem ? 'Partial Delivered' : 'No';
      if (ndelivered < nordered) return hasPartialDeliveredItem ? 'Partial Delivered' : 'Partial';
      return 'Yes';
    },
    getAllDeliveredBadgeClass: function (item) {
      const s = this.getAllDeliveredStatus(item);
      if (s === 'Yes') return 'is-teal';
      if (s === 'Partial') return 'is-warn';
      if (s === 'Partial Delivered') return 'is-cc';
      return '';
    },
    chunk_n: function () { /* no-op, kept for parity */ },
    get_bg_color: function (item) {
      if (item && Number(item.nordered) !== Number(item.ndelivered) && Number(item.ndelivered) > 0) {
        return '#FFF4DC';
      }
      return 'transparent';
    },
    is_action_disabled: function (e) {
      let status = false;
      if (e && e.preventDefault) e.preventDefault();
      let userrole = this.packet && this.packet.userDetails ? this.packet.userDetails.userrole : null;
      if (['locationadmin', 'salesperson', 'warehouseuser', 'warehouseman'].includes(userrole)) status = true;
      return status;
    },
    check_complete_allowed: function (item) {
      if (!item) return false;
      let status = true;
      const pms = Array.isArray(item.payment_methods) ? item.payment_methods : [];
      const financePms = pms.filter(d => d.value === 'finance');
      if (financePms.length > 0) {
        let fund_dates = financePms.map(d => d.fund_date);
        if ((fund_dates.includes(null) || fund_dates.includes(NaN) || fund_dates.includes(undefined)) ||
          item.finance_status == false || item.deposit_status == false ||
          (item.nordered != item.ndelivered)) {
          status = false;
        } else { status = true; }
      } else {
        if (item.nordered != item.ndelivered) status = false;
        else status = true;
      }
      return status;
    },
    update_pm_fund_date: function (item) {
      let that = this;
      let path = "/api/sales/update_payment_method/"
      let data = item;
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          that.$bvToast.toast("Successfully updated fund date.", { title: "Success", autoHideDelay: 1500, variant: "success" });
        }
      }
      let f = function () {
        that.$bvToast.toast("Error updating fund date.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      }
      Requestor.postAsyncP(path, data, this, s, f);
    },
    get_pm_color: function (pmval) {
      const key = safeString(pmval);
      return this.payment_methods_colors[key] || '#f8f9fa';
    },
    change_invoice_completed_status: function (item) {
      let data = { invoice_id: item.invoice_id, status: false }
      if (item.completed == true) data.status = false;
      else data.status = true;
      let that = this;
      let path = "/api/sales/change_invoice_completed_status/"
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          that.$bvToast.toast("Successfully updated invoice.", { title: "Success", autoHideDelay: 1500, variant: "success" });
          item.completed = data.status;
          // If un-completed on this page, drop it from the list
          if (data.status === false) {
            that.allItems = that.allItems.filter(i => i.invoice_id !== item.invoice_id);
            that.items = that.allItems;
          }
        }
        that.items_being_loaded = false;
      }
      let f = function () {
        that.$bvToast.toast("Error updating invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        that.items_being_loaded = false
      }
      this.items_being_loaded = true;
      Requestor.postAsyncP(path, data, this, s, f);
    },
    get_all_items_completed_invoices: function () {
      if (!this.hasMeaningfulFilter) {
        this.allItems = [];
        this.items = [];
        this.items_being_loaded = false;
        this.hasFetchedOnce = false;
        return;
      }

      this.startApiLoading('Loading Completed Orders');

      let data = {};
      if (this.activeFilters.startDate || this.activeFilters.endDate) {
        data.startDate = this.activeFilters.startDate;
        data.endDate = this.activeFilters.endDate;
      } else if (this.activeFilters.invoice_id) {
        data.invoice_id = this.activeFilters.invoice_id;
      }
      if (this.packet && this.packet.userDetails && this.packet.userDetails.properties && this.packet.userDetails.properties.location_id) {
        data.location_id = this.packet.userDetails.properties.location_id;
      }

      // FORCE completed
      data.delivery_status = 'completed';

      if (this.activeFilters.finance_payment_process_filter && !this.searchMode) {
        data.finance_payment_process_filter = this.activeFilters.finance_payment_process_filter;
      }
      if (this.activeFilters.payment_received_simple_filter && !this.searchMode) {
        data.payment_received_filter = this.activeFilters.payment_received_simple_filter;
      }

      let that = this;
      let path = "/api/sales/get_all_items_delivered_invoices/";
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          if (response && response.data && response.data.result && Array.isArray(response.data.result)) {
            const normalized = response.data.result.map(rawItem => {
              const item = rawItem && typeof rawItem === 'object' ? rawItem : {};
              item.invoice_id = item.invoice_id !== null && item.invoice_id !== undefined ? item.invoice_id : '';
              item.fullname = item.fullname !== null && item.fullname !== undefined ? String(item.fullname) : '';
              item.phone = item.phone !== null && item.phone !== undefined ? String(item.phone) : '';
              item.email = item.email !== null && item.email !== undefined ? String(item.email) : '';
              item.zipcode = item.zipcode !== null && item.zipcode !== undefined ? String(item.zipcode) : '';
              item.state = item.state !== null && item.state !== undefined ? String(item.state) : '';
              item.nordered = safeNumber(item.nordered, 0);
              item.ndelivered = safeNumber(item.ndelivered, 0);
              item.completed = !!item.completed;
              item.finance_status = item.finance_status === true;
              item.deposit_status = item.deposit_status === true;
              item.item_states = Array.isArray(item.item_states) ? item.item_states : [];
              item.order_types = Array.isArray(item.order_types) ? item.order_types : [];
              item.delivery_dates = Array.isArray(item.delivery_dates)
                ? item.delivery_dates.map(d => d === null || d === undefined ? '' : String(d))
                : [];
              if (item.order_types_display === null || item.order_types_display === undefined) item.order_types_display = null;
              else item.order_types_display = String(item.order_types_display);
              item.payment_methods = Array.isArray(item.payment_methods)
                ? item.payment_methods.map(normalizePaymentMethod)
                : [];
              return item;
            }).filter(i => i.completed === true); // safety net

            that.allItems = normalized;
            that.items = normalized;
            that.hasFetchedOnce = true;
          } else {
            that.allItems = [];
            that.items = [];
            that.hasFetchedOnce = true;
          }
        }
        that.items_being_loaded = false;
        that.stopApiLoading();
      };
      let f = function (error) {
        console.error('Error loading completed orders:', error);
        that.$bvToast.toast("Error loading completed orders. Please try again later.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        that.items_being_loaded = false;
        that.stopApiLoading();
      };
      this.items_being_loaded = true;
      that.allItems = [];
      that.items = [];
      Requestor.getSync(path, data, s, f);
    },
    sortby: function (key) {
      if (this.sort_asc) this.allItems = Requestor.sort(this.allItems, key)
      else this.allItems = Requestor.sort_reverse(this.allItems, key)
      this.sort_asc = !this.sort_asc;
    },
    openDisputeModal: function (item) {
      this.disputeForm = {
        invoice_id: item.invoice_id, dispute_type: null, customer_statement: null,
        issue_type: null, resolution: null, money_lost: null, details: null,
        image: null, image_preview: null
      };
      this.formSubmitted = false;
      this.showDisputeModal = true;
    },
    submitDispute: function (bvModalEvt) {
      bvModalEvt.preventDefault();
      this.formSubmitted = true;
      if (!this.disputeForm.dispute_type ||
        !this.disputeForm.customer_statement ||
        !this.disputeForm.issue_type ||
        !this.disputeForm.resolution ||
        this.disputeForm.money_lost === null) {
        this.$bvToast.toast("Please fill in all required fields including Dispute Type.",
          { title: "Validation Error", autoHideDelay: 3000, variant: "warning" });
        return;
      }
      let formData = new FormData();
      formData.append("invoice_id", this.disputeForm.invoice_id);
      formData.append("dispute_type", this.disputeForm.dispute_type);
      formData.append("customer_statement", this.disputeForm.customer_statement);
      formData.append("issue_type", this.disputeForm.issue_type);
      formData.append("resolution", this.disputeForm.resolution);
      formData.append("money_lost", this.disputeForm.money_lost);
      formData.append("details", this.disputeForm.details || "");
      if (this.disputeForm.image) formData.append("image", this.disputeForm.image);
      let that = this;
      let path = "/api/sales/create_dispute/";
      let s = function (response) {
        if (response && response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          that.$bvToast.toast("Dispute submitted successfully.", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.showDisputeModal = false;
          that.resetDisputeForm();
          const item = that.allItems.find(i => i.invoice_id === that.disputeForm.invoice_id);
          if (item) { item.finance_status = false; item.deposit_status = false; }
        }
      };
      let f = function (error) {
        console.error('Error submitting dispute:', error);
        that.$bvToast.toast("Error submitting dispute. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
      };
      Requestor.postAsyncP(path, formData, this, s, f);
    }
  },
  created: function () {
    try { this.packet = JSON.parse(localStorage.packet) || {}; } catch (e) { this.packet = {}; }
    try { this.packet.userDetails = JSON.parse(localStorage.userDetails) || {}; } catch (e) { this.packet.userDetails = {}; }
    if (!this.packet.locsubsdict) this.packet.locsubsdict = {};
    if (!Array.isArray(this.packet.locations)) this.packet.locations = [];
    this.packet.locations = this.packet.locations.sort(function (a, b) {
      return (a.is_main_warehouse || 0) - (b.is_main_warehouse || 0);
    });
    // NO auto-fetch — page stays empty until a filter is applied
    this.allItems = [];
    this.items = [];
    this.hasFetchedOnce = false;
  },
  mounted: function () {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
    let that = this;
    const dateEl = document.getElementById('dates');
    if (dateEl) {
      this.datePickerInstance = new Lightpick({
        field: dateEl,
        singleDate: false,
        repick: true,
        startDate: null,
        endDate: null,
        onSelect: function (start, end) {
          that.searchForm.startDate = start ? start.startOf('day').format('YYYY-MM-DD') : '';
          that.searchForm.endDate = end ? end.endOf('day').format('YYYY-MM-DD') : '';
          that.onFilterChange();
          const dateInput = document.getElementById('dates');
          if (dateInput) {
            if (start && end) dateInput.placeholder = `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`;
            else if (start) dateInput.placeholder = start.format('YYYY-MM-DD');
            else dateInput.placeholder = 'Select Date Range';
          }
        }
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.stopLoadingAnimation(false);
    if (this.autoSaveTimeout) clearTimeout(this.autoSaveTimeout);
    if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
    if (this.datePickerInstance) {
      this.datePickerInstance.destroy();
      this.datePickerInstance = null;
    }
  },
}
</script>

<style scoped>
/* Reuse the exact same styles from AdminToolsDeliveredOrders.vue */
.ao-page { background: #F6F4F0; min-height: 100vh; padding: 18px 0 60px; font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #221F1C; font-size: 17px; }
.ao-wrap { max-width: 100%; width: 99%; margin: 0 auto; }
@media (max-width: 767px) {
  .ao-page { padding: 10px 0 40px; font-size: 16px; }
  .ao-wrap { width: 100%; padding: 0 12px; }
}

.ao-loading-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(34, 31, 28, 0.82); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; }
.ao-loading-card { background: #fff; padding: 36px 50px; border-radius: 20px; text-align: center; border-bottom: 5px solid #EE7A22; min-width: 360px; }
.ao-logo { width: 60px; height: 60px; border-radius: 14px; background: #EE7A22; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-family: 'Sora', sans-serif; font-weight: 800; font-size: 24px; }
.ao-logo--lg { width: 72px; height: 72px; font-size: 28px; margin-bottom: 18px; animation: aoPulse 1.2s ease-in-out infinite; }
@keyframes aoPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.ao-loading-text { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin-bottom: 18px; }
.ao-loading-bar { width: 300px; height: 8px; background: #F1EEE9; border-radius: 4px; overflow: hidden; margin: 0 auto; }
.ao-loading-fill { height: 100%; background: #EE7A22; border-radius: 4px; transition: width 0.2s ease; }
.ao-loading-tip { margin-top: 18px; font-size: 15px; color: #736C63; font-weight: 600; }

.ao-fade-enter-active, .ao-fade-leave-active { transition: opacity 0.25s ease; }
.ao-fade-enter, .ao-fade-leave-to { opacity: 0; }
.ao-fade-up-enter-active { animation: aoSlideUp 0.4s ease-out; }
@keyframes aoSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.ao-hero { background: #221F1C; color: #fff; border-radius: 20px; overflow: hidden; border-bottom: 5px solid #EE7A22; margin-bottom: 14px; }
.ao-hero-actions { display: flex; gap: 10px; flex-wrap: wrap; padding: 12px 30px; background: #34302B; }
.ao-hero-actions .btn { padding: 9px 18px !important; font-size: 16px !important; }

.ao-btn-primary.btn, .ao-btn-ghost.btn, .ao-btn-ghost-dark.btn, .ao-btn-orange.btn { border-radius: 10px !important; font-weight: 700; font-size: 16.5px; padding: 10px 18px; display: inline-flex; align-items: center; gap: 8px; box-shadow: none !important; }
.ao-btn-primary.btn { background: #EE7A22 !important; border: 1.5px solid #EE7A22 !important; color: #fff !important; }
.ao-btn-primary.btn:hover:not(:disabled) { background: #D4620F !important; border-color: #D4620F !important; }
.ao-btn-ghost.btn { background: #fff !important; color: #221F1C !important; border: 1.5px solid #E5E1DA !important; }
.ao-btn-ghost-dark.btn { background: rgba(255, 255, 255, 0.06) !important; color: #fff !important; border: 1.5px solid rgba(255, 255, 255, 0.22) !important; }
.ao-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.ao-page .btn:disabled { opacity: 0.5; }

.ao-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; padding: 14px 16px; margin-bottom: 14px; }

.ao-filterstrip { padding: 12px 14px !important; }
.ao-filters-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ao-fb-item { flex: 0 0 auto; min-width: 0; }
.ao-fb-search { flex: 1 1 240px; min-width: 200px; }
.ao-fb-dates { flex: 0 1 240px; min-width: 200px; display: flex; align-items: center; }
.ao-fb-dateinput >>> .form-control, .ao-fb-dateinput >>> input { height: 44px; font-size: 14px; font-weight: 600; border: 1.5px solid #E5E1DA; border-radius: 10px; background: #F7F5F2; padding: 0 14px; box-shadow: none; width: 100%; }
.ao-fb-dateinput >>> input:focus { border-color: #EE7A22; background: #fff; box-shadow: none; }
.ao-fb-inputgroup { flex-wrap: nowrap; width: 100%; }
.ao-fb-inputgroup >>> .input-group-text { height: 44px; padding: 0 12px; background: #F1EEE9; border: 1.5px solid #E5E1DA; border-right: 0; border-radius: 10px 0 0 10px !important; color: #736C63; font-size: 16px; }
.ao-fb-inputgroup >>> .form-control { height: 44px !important; font-size: 15px !important; font-weight: 600 !important; border: 1.5px solid #E5E1DA !important; background: #F7F5F2 !important; color: #221F1C !important; border-radius: 0 10px 10px 0 !important; padding: 0 14px !important; }
.ao-fb-inputgroup >>> .form-control:focus { border-color: #EE7A22 !important; background: #fff !important; box-shadow: none !important; }
.ao-fb-pill { display: inline-flex; align-items: center; gap: 6px; background: #F7F5F2; border: 1.5px solid #E5E1DA; border-radius: 999px; padding: 0 12px 0 10px; height: 44px; flex-shrink: 0; }
.ao-fb-pill-icon { color: #EE7A22; font-size: 16px; flex-shrink: 0; }
.ao-fb-select { border: 0 !important; background: transparent !important; box-shadow: none !important; font-size: 14px !important; font-weight: 700 !important; color: #221F1C !important; padding: 0 22px 0 4px !important; height: 42px !important; min-width: 130px; background-position: right 4px center !important; background-size: 13px !important; cursor: pointer; }
.ao-fb-select:focus { box-shadow: none !important; outline: none !important; }
.ao-result-count { margin-top: 10px; padding-top: 10px; border-top: 1px solid #F1EEE9; font-size: 14px; color: #736C63; font-weight: 600; }
.ao-result-count strong { color: #221F1C; }
.ao-result-count-filtered { color: #D4620F; }
@media (max-width: 900px) {
  .ao-filters-bar { flex-wrap: wrap; }
  .ao-fb-search, .ao-fb-dates { flex: 1 1 100%; min-width: 0; }
  .ao-fb-pill { flex: 1 1 calc(50% - 5px); }
  .ao-fb-select { min-width: 0; width: 100%; }
}

.glass-table-container { background: rgba(255, 255, 255, 0.75); backdrop-filter: blur(6px); border-radius: 16px; padding: 8px; position: relative; width: 100%; overflow: hidden; }
.ao-details-table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 13px; }
.ipt_tdhr2 { background: #221F1C; color: #fff; font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 10px 4px; text-align: center; word-break: break-word; white-space: normal; line-height: 1.2; border-right: 1px solid rgba(255,255,255,0.06); vertical-align: middle; }
.ipt_tdhr2:last-child { border-right: 0; }
.ipt_tdhr2:hover { background: #34302B; }
.ao-sort-icon { opacity: 0.6; font-size: 10px; margin-left: 2px; }
.ipt_tdr3 { text-align: center; font-size: 12px; padding: 8px 4px; background: #FAF9F7; border-bottom: 1px solid #F1EEE9; border-right: 1px solid #F1EEE9; vertical-align: top; word-break: break-word; overflow-wrap: anywhere; }
.ipt_tdr3:last-child { border-right: 0; }
.ao-delivered-table tbody tr:hover .ipt_tdr3 { background: #F7F5F2; }
.discrepancy-row .ipt_tdr3 { background: rgba(201, 60, 48, 0.12) !important; }
.discrepancy-row { box-shadow: inset 4px 0 0 #C93C30; }
.ao-delivered-table tfoot tr td { background: #221F1C !important; color: #fff !important; font-weight: 700; font-size: 13px; }
.fade-in-row { animation: aoRowIn 0.28s ease-out forwards; opacity: 0; }
@keyframes aoRowIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
.invoice-chip { display: inline-block; font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; color: #D4620F; background: #FFF1E4; border: 1px solid #F5CFAE; border-radius: 6px; padding: 3px 6px; word-break: break-all; }
.ao-cust-name { font-weight: 700; font-size: 22px; line-height: 1.25; word-break: break-word; }
.ao-mini-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 16px; font-weight: 800; padding: 3px 8px; border-radius: 999px; background: #F1EEE9; color: #4A4540; border: 1px solid #E5E1DA; white-space: nowrap; }
.ao-mini-chip.is-teal { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ao-mini-chip.is-warn { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ao-mini-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.ao-mini-chip.is-cc { background: #E8EFF7; color: #234A66; border-color: #BBD5E8; }
.ao-deliv-chip { display: inline-block; background: #FFF4DC; color: #8A5E09; font-weight: 700; font-size: 16px; padding: 2px 6px; border-radius: 999px; border: 1px solid #F2DBA5; margin: 1px 0; white-space: nowrap; }
.ao-muted { font-size: 11px; color: #A59D92; font-style: italic; }
.ao-pm-box { padding: 5px 6px; border-radius: 6px; margin: 3px 0; font-size: 16px; line-height: 1.4; text-align: left; word-break: break-word; }
.ao-pm-row { display: flex; gap: 6px; align-items: center; margin-top: 3px; flex-wrap: wrap; }
.ao-pm-input >>> .form-control { height: 24px; font-size: 11px; font-weight: 600; border: 1.5px solid #E5E1DA; border-radius: 6px; width: 66px; display: inline-block; padding: 0 4px; }
.ao-mini-btn.btn, .ao-mini-btn { background: #fff; color: #221F1C; border: 1.5px solid #E5E1DA; border-radius: 7px; font-size: 10.5px; font-weight: 700; padding: 5px 8px; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; box-shadow: none; transition: all 0.15s ease; line-height: 1.2; white-space: nowrap; }
.ao-mini-btn:hover { border-color: #EE7A22; color: #D4620F; transform: translateY(-1px); }
.ao-mini-btn.is-primary { background: #0F6E6B; border-color: #0F6E6B; color: #fff; }
.ao-mini-btn.is-primary:hover { background: #0B5552; border-color: #0B5552; color: #fff; }
.ao-detail-datepicker >>> .mx-input { height: 26px !important; font-size: 10.5px !important; border: 1.5px solid #E5E1DA; border-radius: 6px; width: 100% !important; max-width: 130px; text-align: center; padding: 0 4px; }
.ao-detail-datepicker { width: 100%; display: block; }

.ao-empty { text-align: center; padding: 60px 30px; border: 2px dashed #D6CFC4 !important; background: #FAF9F7 !important; }
.ao-empty-icon { width: 78px; height: 78px; border-radius: 22px; background: #FFF1E4; color: #EE7A22; display: inline-flex; align-items: center; justify-content: center; font-size: 34px; margin-bottom: 18px; }
.ao-empty h3 { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; margin: 0 0 8px; }
.ao-empty p { color: #736C63; margin: 0 auto; max-width: 50ch; line-height: 1.6; font-size: 17px; }

.ao-modal.modal-content { border: 0; border-radius: 20px; overflow: hidden; box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5); }
.ao-modal .modal-header { background: #221F1C !important; color: #fff !important; border: 0 !important; border-bottom: 4px solid #EE7A22 !important; padding: 18px 26px !important; }
.ao-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.ao-modal .modal-body { background: #F6F4F0; padding: 22px 24px; }
.ao-modal .modal-footer { background: #F6F4F0; border-top: 1px solid #E5E1DA; padding: 14px 24px; }
.ao-modal-title { display: flex; align-items: center; gap: 16px; }
.ao-modal-title-icon { width: 48px; height: 48px; border-radius: 12px; background: rgba(238, 122, 34, 0.2); color: #FFB06B; display: inline-flex; align-items: center; justify-content: center; font-size: 20px; }
.ao-modal-title-icon.is-warn { background: rgba(201, 138, 18, 0.25); color: #FFD79A; }
.ao-modal-title-main { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; }
.ao-modal-title-sub { font-size: 14px; color: rgba(255, 255, 255, 0.65); }
.ao-dispute-form >>> .form-group label { font-size: 13px; font-weight: 800; color: #736C63; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
.ao-detail-select-wide >>> .custom-select { height: 44px; font-size: 15px; font-weight: 600; border: 1.5px solid #E5E1DA; border-radius: 10px; min-width: 100%; }
.ao-fb-input >>> .form-control { height: 44px; font-size: 15px; font-weight: 600; border: 1.5px solid #E5E1DA; border-radius: 10px; background: #F7F5F2; }
.ao-fb-input >>> .form-control:focus { border-color: #EE7A22; background: #fff; box-shadow: none; }

.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
.mb-2 { margin-bottom: 0.5rem; }
.text-right { text-align: right; }
.text-left { text-align: left; }

@media (prefers-reduced-motion: reduce) {
  .ao-logo--lg, .fade-in-row { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsCompletedOrdersPage.vue